import moment, { Moment } from 'moment'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

import DateTime from '@/components/Form/DateTimePicker/DateTime'
import defaultProps from '@/components/Form/defaultProps'
import propTypes, { formik } from '@/components/Form/propTypes'

import messages from './messages'

import './index.scss'

import { get } from 'lodash'
import { usePrevious } from 'react-use'

function DateTimeGroup(props: any) {
  const intl = useIntl()

  const [state, _setState] = useState(getInitialState(props))
  const setState = (update: Partial<Parameters<typeof _setState>[0]>) => {
    return _setState(state => ({ ...state, ...(typeof update === 'function' ? update(state) : update) }))
  }

  const getCurrentDateValue = () => {
    const value = get(props.formik.values, props.name, '')
    if (isValidDateTime(value)) {
      return moment(value, moment.ISO_8601).local()
    }
  }

  const getCurrentTimeValue = () => {
    const value = get(props.formik.values, props.name, '')
    if (isValidDateTime(value)) {
      return moment(value, moment.ISO_8601).local()
    }
  }

  /**
   * Generates the state object in response to a props object. An invalid date value will cause the time field to be
   * disabled. A valid ISO8601 date value will be converted to the local timezone and set both the date and time fields.
   */
  function getInitialState(props: any) {
    let dateValue: Moment | string = ''
    let timeDisabled = false
    let timeValue: Moment | string = ''
    const timeVisible = get(props, 'showTime', false)

    if (props.value !== '' && !isValidDateTime(props.value)) {
      dateValue = props.value
      timeDisabled = true
    } else if (props.value !== '') {
      dateValue = moment(props.value, moment.ISO_8601).local()
      timeValue = dateValue.clone()
    }

    return {
      dateValue,
      timeDisabled,
      timeValue,
      timeVisible
    }
  }

  const handleTouched = () => {
    // mark the field touched but don't initiate formik validation
    props.formik.setFieldTouched(props.name, true, true)
  }

  function isValidDateTime(value: any) {
    if (typeof value === 'string') {
      value = moment(value, moment.ISO_8601)
    }

    return value.isValid()
  }

  const isMomentTodaysDate = (value: any) => {
    if (!isValidDateTime(value)) {
      return false
    }

    return moment().isSame(value, 'day')
  }

  /**
   * Controls the date field in response to it being made visible. The time field is disabled if the date value is
   * invalid. The time field will default to the current time if the date is today otherwise midnight.
   */
  const onAddTimeClick = () => {
    let timeDisabled = false
    let timeValue = state.timeValue

    if (!isValidDateTime(state.dateValue)) {
      timeDisabled = true
      timeValue = ''
    } else if (!state.timeValue) {
      timeValue = isMomentTodaysDate(state.dateValue)
        ? moment()
        : moment({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    }

    setState({ timeDisabled, timeValue, timeVisible: true })
  }

  /**
   * Fires the onChange event in response to a date or time value change. Returns an empty string if the date value is
   * the default of empty string or if either the start or time values are invalid. Otherwise a singular ISO8601 string
   * is constructed from the separate date and time values. The date and time values are local timezones so converting
   * to UTC may cause date wrapping.
   */
  const prevState = usePrevious(state)
  useEffect(() => {
    if (!prevState) return // Only run on update

    let value
    const { dateValue, timeValue } = state

    if (dateValue === '' || !isValidDateTime(dateValue)) {
      props.formik.setFieldValue(props.name, 'invalid')
      return
    }

    value = moment({
      // @ts-expect-error
      year: dateValue.year(),
      // @ts-expect-error
      month: dateValue.month(),
      // @ts-expect-error
      date: dateValue.date(),
      // @ts-expect-error
      hour: dateValue.hour(),
      // @ts-expect-error
      minute: dateValue.minute(),
      // @ts-expect-error
      second: dateValue.second()
    })

    if (!props.timeOverride) {
      if (!isValidDateTime(timeValue)) {
        props.formik.setFieldValue(props.name, 'invalid')
        return
      }

      value = value.set({
        // @ts-expect-error
        hour: timeValue.hour(),
        // @ts-expect-error
        minute: timeValue.minute(),
        // @ts-expect-error
        second: timeValue.second()
      })
    }

    if (props.onChange) {
      props.onChange(value)
    }

    props.formik.setFieldValue(props.name, value.toISOString())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  /**
   * Sets the date value in response to date field manual input or picker selection. Disables the time field if the date
   * is invalid. If the time field isn't already visible, the time value is defaulted to either the current time if the
   * selected date is today otherwise midnight local time
   *
   * @param value moment object if valid input value else a string
   */
  const onDateChange = (value: any) => {
    if (typeof value === 'string') {
      setState({
        dateValue: value,
        timeDisabled: true
      })
      return
    }

    let timeValue = state.timeValue

    if (!state.timeVisible || timeValue === '') {
      if (props.defaultTime) {
        timeValue = moment(props.defaultTime)
      } else if (isMomentTodaysDate(value)) {
        timeValue = moment()
      } else {
        timeValue = moment({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
      }
    }

    setState({
      dateValue: value.local(),
      timeDisabled: false,
      timeValue
    })
  }

  /**
   * Sets the time value in response to time field manual input or picker selection
   *
   * @param value moment object if valid input value else a string
   */
  const onTimeChange = (value: any) => {
    setState({
      timeValue: typeof value === 'string' ? value : value.local()
    })
  }

  function renderTimeField() {
    if (props.includeTime) {
      return state.timeVisible ? (
        <DateTime
          {...props}
          disabled={state.timeDisabled}
          id={`${props.name}__time`}
          isDatePicker={false}
          onBlur={handleTouched}
          onChange={onTimeChange}
          value={getCurrentTimeValue()}
        />
      ) : (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/anchor-is-valid
        <a onClick={onAddTimeClick}>{intl.formatMessage(messages.addTime)}</a>
      )
    }
  }

  return (
    <div className="form__datepicker_group">
      <DateTime
        {...props}
        id={`${props.name}__date`}
        isTimePicker={props.timeOverride}
        isValidDate={props.isValid}
        onBlur={handleTouched}
        onChange={onDateChange}
        value={getCurrentDateValue()}
      />

      {renderTimeField()}
    </div>
  )
}

DateTimeGroup.displayName = 'DateTimeGroup'

DateTimeGroup.propTypes = {
  ...propTypes,
  formik: formik.isRequired,
  /**
   * optional callback for each date visible in a picker to determine if it's a valid selection or not. function takes
   * arguments of `currentDate`, which is the date to validate, and `selectedDate`, the already selected value in the
   * picker, and must return a boolean
   */
  isValid: PropTypes.func,

  defaultTime: PropTypes.shape({
    hours: PropTypes.number,
    minutes: PropTypes.number,
    seconds: PropTypes.number,
    milliseconds: PropTypes.number
  }),
  /**
   * Whether the time field should be shown by default
   */
  includeTime: PropTypes.bool,
  onChange: PropTypes.func,
  showTime: PropTypes.bool,
  timeOverride: PropTypes.bool
}

DateTimeGroup.defaultProps = {
  ...defaultProps,
  isValid: () => true,
  includeTime: false,
  onChange: () => {},
  timeOverride: false
}

export default DateTimeGroup
